import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { meKey } from './auth';
import type {
  CreateWorkspaceInput,
  DataEnvelope,
  ListEnvelope,
  UpdateCurrentWorkspaceInput,
  UpdateWorkspaceInput,
  User,
  Workspace,
  WorkspaceStatus,
  WorkspaceWithStats,
} from './types';

export interface WorkspaceFilters {
  q?: string;
  status?: WorkspaceStatus | '';
}

/** Platform console data lives under its own key so switching workspaces never clears it by accident. */
export const workspacesKey = (filters: WorkspaceFilters = {}) => ['platform', 'workspaces', filters] as const;

export function useWorkspacesInfinite(filters: WorkspaceFilters) {
  return useInfiniteQuery({
    queryKey: [...workspacesKey(filters), 'infinite'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      api<ListEnvelope<WorkspaceWithStats>>('/workspaces', {
        query: { ...filters, limit: 25, cursor: pageParam },
        signal,
      }),
    getNextPageParam: (last) => last.pagination.nextCursor ?? undefined,
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkspaceInput) =>
      api<DataEnvelope<{ workspace: WorkspaceWithStats; owner: User }>>('/workspaces', {
        method: 'POST',
        body: input,
      }).then((r) => r.data),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform'] }),
  });
}

export function useUpdateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateWorkspaceInput }) =>
      api<DataEnvelope<WorkspaceWithStats>>(`/workspaces/${id}`, { method: 'PATCH', body: input }).then(
        (r) => r.data,
      ),
    // Plan / status changes move revenue and client counts on every platform screen.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['platform'] }),
  });
}

/** The workspace the signed-in user works in. */
export function useCurrentWorkspace() {
  return useQuery({
    queryKey: ['workspace', 'current'],
    queryFn: ({ signal }) =>
      api<DataEnvelope<Workspace>>('/workspaces/current', { signal }).then((r) => r.data),
  });
}

export function useUpdateCurrentWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCurrentWorkspaceInput) =>
      api<DataEnvelope<Workspace>>('/workspaces/current', { method: 'PATCH', body: input }).then(
        (r) => r.data,
      ),
    onSuccess: (ws) => {
      qc.setQueryData(['workspace', 'current'], ws);
      void qc.invalidateQueries({ queryKey: meKey });
    },
  });
}
